import React, { useEffect } from 'react'
import styled from 'styled-components'
import { rgba } from 'polished'
import { FormControl, RadioGroup, FormControlLabel, Radio } from '@mui/material'

import { ModalConsumer } from '../../context/ModalContext'
import LinkButton from '../../components/LinkButton'
import SlideContainer from '../../components/Layout/SlideContainer'

import breakpoints from '../../constants/breakpoints'
import theme from '../../theme'
import { Slide } from '../../types'
import { getLocalStorage, getNextSkipRoute, setLocalStorage } from '../../utils'
import Readspeaker from '../../components/Readspeaker'

interface Props {
  routeId: string
  basePath: string
  nextLink: string
  button: string
  subtitle: string
  title: string
  quizAnswers: string[]
  correctQuizAnswer: string
  explanation: string
  modalLink?: string
  modalHeader?: string
  modalText?: string
  routeIndex?: string
  routes: Slide[]
  onSkipRoute: (path: string) => void
}

const breakpointMd = `@media (min-width: ${breakpoints.md})`

const QuizQuestion: React.FC<Props> = ({
  routeId,
  basePath,
  button,
  nextLink,
  subtitle,
  quizAnswers,
  correctQuizAnswer,
  explanation,
  title,
  modalLink,
  modalHeader,
  modalText,
  routeIndex,
  routes,
  onSkipRoute,
}) => {
  const splitTitle = title.split(new RegExp('%MODALLINK%', 'gi'))
  const answersInStorage = getLocalStorage('answers')
  const foundAnswer =
    answersInStorage && answersInStorage.find((answer) => answer.routeId === routeId)
  const [value, setValue] = React.useState({
    routeId: routeId,
    answer: foundAnswer ? foundAnswer.answer[0] : '',
    answeredCorrectly: foundAnswer && foundAnswer.answer[0] === correctQuizAnswer ? true : false,
    isConditionalRoute:
      foundAnswer && foundAnswer.isConditionalRoute ? foundAnswer.isConditionalRoute : false,
  })
  const legendTitle = splitTitle[1] ? splitTitle[0] + modalLink + splitTitle[1] : splitTitle[0]

  const styles = {
    alignItems: 'flex-start',
    backgroundColor: 'white',
    border: 'solid',
    borderColor: theme.lightGrey,
    borderRadius: 1,
    borderWidth: 1,
    boxShadow: 'none',
    color: theme.linkColor,
    fontFamily: theme.primaryFont,
    height: '100%',
    lineHeight: '1.2',
    margin: 0,
    padding: '24px',
    textDecoration: 'none',
    transition: 'all 0.3s ease',
    width: '100%',
    [breakpointMd]: {
      flexDirection: 'column',
      padding: '32px',
      minWidth: '600px',
    },
    '&:hover': {
      boxShadow: '0 2px 10px 0 rgba(30, 30, 30, 0.4)',
      backgroundColor: theme.blue,
      borderColor: theme.blue,
      '& svg': {
        fill: 'white',
      },
      '& span': {
        color: 'white',
      },
    },
    '&:focus-within': {
      outline: '5px auto #005fcc',
      outlineOffset: '5px',
    },
    '&>span': {
      fontSize: '1rem',
      fontFamily: theme.primaryFont,
      [breakpointMd]: {
        fontSize: '1.5rem',
      },
    },
    '& .Mui-checked': {
      color: 'white',
    },
  }

  useEffect(() => {
    const nextRoute = routeIndex && getNextSkipRoute(routeIndex, routes)
    nextRoute && onSkipRoute(nextRoute.path)
  }, [])

  const handleChange = (answer: string, isConditionalRoute = false) => {
    if (answer === correctQuizAnswer) {
      setValue({
        routeId,
        answer: answer,
        answeredCorrectly: true,
        isConditionalRoute,
      })
    } else if (answer !== correctQuizAnswer) {
      setValue({
        routeId,
        answer: answer,
        answeredCorrectly: false,
        isConditionalRoute,
      })
    }
  }

  const handleLocalStorage = () => {
    setLocalStorage(
      { routeId, answer: [value.answer], isConditionalRoute: value.isConditionalRoute },
      basePath,
    )
  }

  return (
    <SlideContainer>
      <Content className="text-formatted">
        <FixedWidth>
          <Subtitle>{subtitle}</Subtitle>
          {splitTitle.length > 1 ? (
            <h1>
              {splitTitle[0]}
              <ModalConsumer>
                {({ openModal }) => (
                  <StyledLink
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      openModal(modalHeader || '', modalText || '')
                    }}
                  >
                    {modalLink}
                  </StyledLink>
                )}
              </ModalConsumer>
              {splitTitle[1]}
            </h1>
          ) : (
            <h1>{title}</h1>
          )}
        </FixedWidth>
        <Readspeaker />

        <FormControl component="fieldset" sx={{ marginTop: '40px' }}>
          <legend className="sr-only">{legendTitle}</legend>
          <RadioGroup
            value={value.answer}
            aria-labelledby={title.replace(/[^A-Z0-9]+/gi, '_')}
            name="customized-radios"
            sx={{
              [breakpointMd]: {
                display: 'flex',
                flexDirection: 'column',
                flexWrap: 'nowrap',
              },
            }}
          >
            {quizAnswers.map((answer: string) => {
              return (
                <ButtonContainer checked={value.answer === answer} key={`quizAnswer-${answer}`}>
                  <FormControlLabel
                    onClick={() => {
                      handleChange(answer)
                    }}
                    onKeyDown={(e) => {
                      if (e.keyCode === 13) {
                        handleChange(answer)
                      }
                    }}
                    value={answer}
                    control={
                      <Radio
                        color="default"
                        sx={{
                          clip: 'rect(1px, 1px, 1px, 1px)',
                          height: '1px',
                          overflow: 'hidden',
                          position: 'absolute',
                          width: '1px',
                          margin: '0',
                        }}
                      />
                    }
                    label={answer}
                    sx={[styles]}
                  />
                </ButtonContainer>
              )
            })}
          </RadioGroup>
        </FormControl>

        {value.answer && (
          <Response
            className="rs_skip"
            role="region"
            aria-live="polite"
            answeredCorrectly={value.answeredCorrectly}
          >
            <ResponsTitle>
              {value.answeredCorrectly ? 'Dat klopt' : 'Het goede antwoord is ' + correctQuizAnswer}
            </ResponsTitle>
            <Explanation>{explanation}</Explanation>
          </Response>
        )}

        {value.answer && nextLink && (
          <Next>
            <LinkButton onClick={handleLocalStorage} to={nextLink}>
              {button}
            </LinkButton>
          </Next>
        )}
      </Content>
    </SlideContainer>
  )
}

const FixedWidth = styled.div`
  max-width: 700px;
`

const Content = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  width: 100%;
  text-align: center;
  padding-bottom: 40px;
`

const StyledLink = styled.a`
  border-color: ${({ theme }) => theme.blue};
  color: ${({ theme }) => theme.blue};
  display: inline-block;
`

const Subtitle = styled.span`
  font-size: 1rem;
  font-weight: bold;
`

const ButtonContainer = styled.div<{ checked: boolean }>`
  width: 100%;
  margin-bottom: 16px;

  @media (min-width: ${breakpoints.md}) {
    margin-bottom: 24px;
    min-width: 160px;
  }

  > .MuiFormControlLabel-root {
    & svg {
      width: 30px;
      height: 30px;
    }
  }

  ${({ checked, theme }) =>
    checked &&
    `
    > .MuiFormControlLabel-root {
      background-color: ${theme.blue};
      color: white;
      box-shadow: 0 2px 10px 0 rgba(30, 30, 30, 0.4);
      border-color: ${theme.blue};

      & svg {
        fill: white;
      }
    }
  `};
`

const Response = styled.div<{ answeredCorrectly: boolean }>`
  padding: 14px 32px;
  margin-top: 16px;
  text-align: left;
  background-color: ${rgba(theme.red, 0.2)};
  border-left: 3px solid ${theme.red};

  ${({ answeredCorrectly, theme }) =>
    answeredCorrectly &&
    `
    border-left: 3px solid ${theme.lightGreen};
    background-color: ${rgba(theme.lightGreen, 0.2)};
`};

  @media (min-width: ${breakpoints.md}) {
    width: 600px;
  }

  & ul {
    margin-top: 0;
  }
`

const ResponsTitle = styled.h2`
  font-size: 18px;
  line-height: 1.6;
  margin-bottom: 0;

  & + * {
    margin-top: 0;
  }
`

const Explanation = styled.p`
  white-space: pre-line;
`

const Next = styled.div`
  margin: 60px auto 0;
  width: 100%;
  max-width: 700px;
  position: fixed;
  box-shadow: 0 -10px 27px 9px rgba(0, 0, 0, 0.06);
  bottom: 0;
  background: white;

  & a {
    margin: 10px 0;
    width: 202px;
    padding: 15px;
  }

  @media (min-width: ${breakpoints.sm}) {
    position: inherit;
    box-shadow: none;

    & a {
      width: 100%;
    }
  }
`

export default QuizQuestion
